import { useState, useMemo, useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { C } from '../lib/tokens.js';
import { requestUrlKey } from '../lib/utils.js';
import { I18N_EN, I18N_NL } from '../lib/data.js';
import { t } from '../lib/i18n.jsx';
import { I18nProvider } from '../lib/i18n.jsx';
import { LookupsContext, useLookups } from '../lib/LookupsContext.jsx';
import { ExtensionSlotsContext, useExtensionSlot } from '../lib/ExtensionSlotsContext.jsx';
import { useToast } from '../lib/ToastContext.jsx';
import { UserCacheProvider } from '../lib/UserCacheContext.jsx';
import { PageSpinner } from '../components/ui/Misc.jsx';
import { getCookie, setCookie } from '../lib/cookies.js';
import { useAppBoot } from '../lib/useAppBoot.js';
import { useOrders, useInvoices, usePayments } from '../lib/useBusinessData.js';
import { ToastProvider } from '../lib/ToastContext.jsx';
import { useRequests } from '../lib/useRequests.js';
import { useLiveEvents } from '../lib/useLiveEvents.js';
import { useSession, rememberPostLoginRedirect } from '../lib/useSession.js';
import { IdleWarningModal, SessionEndingModal } from '../components/session/SessionModals.jsx';
import { useSearchScopeSync } from '../lib/useSearchScopeSync.js';
import { useViewport } from '../lib/useViewport.js';
import { Header, HelpPanel } from '../components/layout/Header.jsx';
import { SideMenu } from '../components/layout/SideMenu.jsx';
import { Breadcrumb } from '../components/layout/Breadcrumb.jsx';
import { TweaksPanel, TweakSection, TweakRadio, TweakToggle, useTweaks } from '../components/tweaks/TweaksPanel.jsx';
import { RequestsPage } from '../pages/requests/RequestsPage.jsx';
import { RequestDetailPage } from '../pages/requests/detail/RequestDetailPage.jsx';
import { RequestPeekPanel } from '../pages/requests/detail/RequestPeekPanel.jsx';
import { CreateRequestPage } from '../pages/create/CreateRequest/CreateRequestPage.jsx';
import { AssetsPage } from '../pages/assets/AssetsPage.jsx';
import { OrdersPage } from '../pages/business/OrdersPage.jsx';
import { InvoicesPage } from '../pages/business/InvoicesPage.jsx';
import { PaymentsPage } from '../pages/business/PaymentsPage.jsx';
import { AccountPage } from '../pages/account/AccountPage.jsx';
import { getDefaultView } from '../lib/viewPref.js';
import { AboutPage } from '../pages/about/AboutPage.jsx';
import { HelpdeskHomePage } from '../pages/home/HelpdeskHomePage.jsx';
import { LoginPage } from '../pages/auth/LoginPage.jsx';

const normalizeView = (v) => (v === 'timeline' ? 'timeline' : 'board');

const TWEAK_DEFAULTS = {
  "view": getDefaultView(),
  "density": "comfortable",
  "showPriority": true,
  "darkHeader": false
};

const PUBLIC_LOCALE_KEY = 'af_public_locale';

// Redirects home when the current web role has no grant for this page.
// /me is already resolved by the time any route renders (see useAppBoot).
function PageGate({ allowed, children }) {
  return allowed ? children : <Navigate to="/" replace />;
}

export function App({ extensions = {} }) {
  const location = useLocation();
  const session = useSession();

  // Logged-out default is always English
  const [publicLocale, setPublicLocale] = useState(() => getCookie(PUBLIC_LOCALE_KEY) || 'en-US');

  function handlePublicLocaleChange(locale) {
    setCookie(PUBLIC_LOCALE_KEY, locale, 365);
    setPublicLocale(locale);
  }

  if (!session.loginId) {
    // A shared/deep link (e.g. straight to a request) hit while logged out — the
    // browser URL is already the intended destination and nothing here navigates
    // away from it, so stash it explicitly rather than relying on that as an
    // accident of no other code touching the URL in this branch.
    rememberPostLoginRedirect(location.pathname + location.search);
    return (
      <I18nProvider initialBundle={publicLocale === 'nl-NL' ? I18N_NL : I18N_EN} locale={publicLocale}>
        <LoginPage onLogin={session.logIn} locale={publicLocale} onLocaleChange={handlePublicLocaleChange} />
      </I18nProvider>
    );
  }

  return (
    <ToastProvider>
      {/* Keyed on the login so a login from another tab (possibly another user) reloads everything. */}
      <AuthenticatedApp key={session.loginId} onLogout={session.logOut} onSessionLimits={session.setSessionLimits}
        locale={publicLocale} extensions={extensions} />
      {session.secondsLeft !== null && (
        <IdleWarningModal secondsLeft={session.secondsLeft} onStay={session.staySignedIn} onLogoutNow={session.logOut} />
      )}
      {session.secondsLeft === null && session.sessionEndNoticeShown && (
        <SessionEndingModal expiresAt={session.sessionExpiresAt} locale={publicLocale} onClose={session.dismissSessionEndNotice} />
      )}
    </ToastProvider>
  );
}

function AuthenticatedApp({ onLogout, onSessionLimits, locale, extensions }) {
  const { me, lookups, loading, refreshRequestClusters } = useAppBoot();

  useEffect(() => {
    onSessionLimits({ timeoutMs: me.sessionTimeoutMs, expiresAt: me.sessionExpiresAt });
  }, [me.sessionTimeoutMs, me.sessionExpiresAt, onSessionLimits]);

  if (loading) {
    return <PageSpinner label="Loading…" />;
  }

  return (
    <I18nProvider initialBundle={locale === 'nl-NL' ? I18N_NL : I18N_EN} locale={locale}>
      <LookupsContext.Provider value={{ ...lookups, refreshRequestClusters }}>
        <ExtensionSlotsContext.Provider value={extensions.slots ?? {}}>
          <UserCacheProvider>
            <AppBody me={me} onLogout={onLogout} extensions={extensions} />
          </UserCacheProvider>
        </ExtensionSlotsContext.Provider>
      </LookupsContext.Provider>
    </I18nProvider>
  );
}

function AppBody({ me, onLogout, extensions }) {
  const { routes: extraRoutes = [], sidebarGroups: rawExtraGroups = [], sidebarChildren } = extensions;
  const role = me.role;
  // Drop agentOnly nav entries for non-agents, and any group left with no
  // items once its entries are filtered (matches PageGate's route-level guard
  // for the same extension pages - see extraRoutes render below).
  const extraGroups = rawExtraGroups
    .map(g => ({ ...g, items: g.items.filter(item => !item.agentOnly || role === 'agent') }))
    .filter(g => g.items.length > 0);
  const { statuses, requestTypes, statusCategories } = useLookups();
  const [tweaks, setTweak] = useTweaks(TWEAK_DEFAULTS);

  const navigate = useNavigate();
  const location = useLocation();

  const currentPath = location.pathname;
  const page = currentPath === '/' ? 'requests' : currentPath.split('/')[1];

  // A saved "list" / "cards" (views that no longer exist) falls back to the board.
  const [view, setView] = useState(normalizeView(tweaks.view));
  const [filters, setFilters] = useState({ q: '', status: '', type: '', range: '', scope: 'mine' });
  const [searchQuery, setSearchQuery] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [peekId, setPeekId] = useState(null);
  const { isMobile } = useViewport();
  const leaveGuardRef = useRef(null);
  const { addToast } = useToast();
  const persistCreateExtra = useExtensionSlot('request.create.persistExtra');

  const {
    requests,
    loadingInitial,
    detailCache,
    closedTotal,
    hasMore,
    loadMore,
    loadingMore,
    search,
    searchResults,
    searchLoading,
    moveRequest,
    updateRequest,
    postTransition,
    createRequest,
    findSimilar,
    addComment,
    applySSEEvent,
    fetchDetail,
    resolveRequestRef,
  } = useRequests(filters);

  // The optional 'request.create.extraFields' value (see CreateRequestPage) needs the new
  // request's id, so it's persisted here once createRequest() resolves. A failure only
  // toasts - the request already exists, and rethrowing would let the user submit it twice.
  // Duplicate check "reply to this ticket" (staff only): the draft becomes a comment on the
  // existing request instead of a new one.
  async function handleCreateReply(id, draft, confidentialType) {
    const ok = await addComment(id, { text: `${draft.summary}\n\n${draft.description}`, confidentialType, files: draft.attachments });
    if (ok) navigate(`/requests/${id}`);
  }

  async function handleCreateSubmit(draft) {
    const newReq = await createRequest(draft);
    if (newReq && persistCreateExtra && draft.extra != null && role === 'agent') {
      try {
        await persistCreateExtra(newReq.id, draft.extra);
      } catch {
        addToast('Request created, but its extra fields could not be saved.');
      }
    }
    navigate(newReq ? `/requests/${requestUrlKey(newReq)}` : '/');
  }

  // Always fetched (not gated by current page) — the sidebar badges (counts.openOrders
  // etc.) need this everywhere, not just while the user is actually on that page.
  const { orders } = useOrders();
  const { invoices } = useInvoices();
  const { payments } = usePayments();

  useLiveEvents(applySSEEvent);

  useEffect(() => { setView(normalizeView(tweaks.view)); }, [tweaks.view]);
  useEffect(() => { setSidebarOpen(false); }, [currentPath]);

  useEffect(() => {
    const t = setTimeout(() => search(filters.q), 350);
    return () => clearTimeout(t);
  }, [filters.q, search]);

  useSearchScopeSync(filters, setFilters, role);

  const isSearchMode = searchResults !== null;
  const displayRequests = isSearchMode ? searchResults : requests;
  const displayHasMore = !isSearchMode && hasMore;
  const displayLoadMore = isSearchMode ? undefined : loadMore;
  const displayLoadingMore = isSearchMode ? searchLoading : loadingMore;

  const closedStatusIds = useMemo(
    () => new Set(statuses.filter(s => s.is_closed || s.terminal).map(s => s.id)),
    [statuses]
  );

  const counts = useMemo(() => ({
    openRequests: requests.filter(r => !closedStatusIds.has(r.status)).length,
  }), [requests, closedStatusIds]);

  const onNav = (id) => {
    if (page === 'create' && leaveGuardRef.current && !leaveGuardRef.current()) return;
    if (id === 'requests' || id === '/') {
      navigate('/');
    } else {
      navigate(`/${id}`);
    }
  };

  const breadcrumbItems = useMemo(() => {
    const base = { label: t('nav.helpdesk'), path: '/' };

    let requestsLabel = t('crumb.my_requests');
    const currentScope = filters.scope || (role === 'agent' ? 'all' : 'mine');

    if (role === 'agent') {
      requestsLabel = currentScope === 'mine' || currentScope === 'waiting' ? t('crumb.my_requests') : t('crumb.all_requests');
    } else {
      requestsLabel = currentScope === 'company' ? t('crumb.whole_company') : t('crumb.my_requests');
    }

    if (page === 'home') {
      return [{ label: t('nav.helpdesk'), path: null }];
    }

    if (currentPath.startsWith('/requests/')) {
      const requestId = currentPath.split('/')[2];
      const cameFromCluster = location.state?.from === 'request-cluster';
      const cameFromProjectManagement = location.state?.from === 'project-management';
      const cameFromTeamActivity = location.state?.from === 'team-activity';
      let parent;
      if (cameFromCluster) {
        parent = {
          label: t('nav.request_cluster'),
          path: location.state?.clusterId
            ? `/request-cluster?cluster=${location.state.clusterId}`
            : '/request-cluster',
        };
      } else if (cameFromProjectManagement) {
        const path = location.state?.requestTab === 'unclustered'
          ? `/project-management?tab=unclustered&request=${requestId}`
          : location.state?.clusterId
            ? `/project-management?cluster=${location.state.clusterId}`
            : '/project-management';
        parent = { label: 'Project Management', path };
      } else if (cameFromTeamActivity) {
        parent = { label: 'Team Activity', path: '/team-activity' };
      } else {
        parent = { label: requestsLabel, path: '/' };
      }
      // Following a linked request grows this trail by one entry each hop, so the
      // breadcrumb can walk back through however many linked requests were followed,
      // not just the single most-recent one.
      const trail = location.state?.requestTrail ?? [];
      const trailItems = trail.map((entry, i) => ({
        label: `#${entry.docNumber || entry.id}`,
        path: `/requests/${requestUrlKey(entry)}`,
        state: { requestTrail: trail.slice(0, i) },
      }));
      return [
        base,
        parent,
        ...trailItems,
        { label: `#${requestId}`, path: currentPath }
      ];
    }

    // A specific day (?id=...) is a sub-page of /timesheets, not a page of its
    // own - same pattern as /requests/:id above, but state-based (no route
    // param) since the list/detail toggle lives entirely in a query string.
    if (currentPath === '/timesheets') {
      const params = new URLSearchParams(location.search);
      const timesheetId = params.get('id');
      const crumbs = [base, { label: breadcrumbLabel('timesheets', role), path: timesheetId ? '/timesheets' : null }];
      if (timesheetId) {
        crumbs.push({ label: location.state?.timesheetLabel || `#${timesheetId}`, path: currentPath + location.search });
      }
      return crumbs;
    }

    return [
      base,
      {
        label: page === 'requests' ? requestsLabel : breadcrumbLabel(page, role),
        path: page === 'requests' ? '/' : `/${page}`
      }
    ];
  }, [currentPath, page, role, filters.scope, location.state, location.search]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', background: C.platinum }}>
      <Header
        user={me} role={role} dark={tweaks.darkHeader} onNew={() => navigate('/create')}
        onHelp={() => setShowHelp(true)} onLogout={onLogout} onSettings={() => navigate('/account')}
        searchQuery={searchQuery} onSearch={setSearchQuery}
        searchData={{ requests, orders, invoices, payments }}
        onMenuClick={() => setSidebarOpen(true)}
        onResultSelect={({ kind, id, docNumber }) => {
          if (page === 'create' && leaveGuardRef.current && !leaveGuardRef.current()) return;
          setSearchQuery('');
          if (kind === 'request')      navigate(`/requests/${docNumber || id}`);
          else if (kind === 'order')   navigate('/orders');
          else if (kind === 'invoice') navigate('/invoices');
          else if (kind === 'payment') navigate('/payments');
        }}
      />

      <Breadcrumb items={breadcrumbItems} />

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <SideMenu active={currentPath.startsWith('/requests/') ? 'requests' : page} onSelect={onNav} role={role} counts={counts} me={me} extraGroups={extraGroups}
          isMobile={isMobile} open={sidebarOpen} onClose={() => setSidebarOpen(false)}>{sidebarChildren}</SideMenu>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, overflow: 'hidden' }}>

          <Routes>
            <Route path="/" element={
              <RequestsPage
                requests={requests} loading={loadingInitial} isSearchMode={isSearchMode} searchResults={searchResults} searchLoading={searchLoading}
                filters={filters} setFilters={setFilters} role={role} me={me}
                statusCategories={statusCategories} requestTypes={requestTypes}
                closedStatusIds={closedStatusIds} closedTotal={closedTotal}
                view={view} onViewChange={v => { setView(v); setTweak('view', v); }} tweaks={tweaks}
                onOpen={req => navigate(`/requests/${requestUrlKey(req)}`)} onPeek={req => setPeekId(req.id)} onMove={moveRequest}
                hasMore={displayHasMore} onLoadMore={displayLoadMore} loadingMore={displayLoadingMore}
              />
            } />

            <Route path="/requests/:id" element={
              <RequestDetailPage
                detailCache={detailCache}
                role={role}
                me={me}
                fetchDetail={fetchDetail}
                resolveRequestRef={resolveRequestRef}
                updateRequest={updateRequest}
                postTransition={postTransition}
                addComment={addComment}
                tweaks={tweaks}
              />
            } />

            <Route path="/home" element={<HelpdeskHomePage />} />
            <Route path="/create" element={<CreateRequestPage role={role} me={me} onCancel={() => navigate('/')} onSubmit={handleCreateSubmit} onFindSimilar={findSimilar} onReply={handleCreateReply} onRegisterGuard={fn => { leaveGuardRef.current = fn; }} />} />
            <Route path="/assets" element={<PageGate allowed={me.canViewSystemTimeline}><AssetsPage role={role} /></PageGate>} />
            <Route path="/orders" element={<PageGate allowed={me.canViewOrders}><OrdersPage /></PageGate>} />
            <Route path="/invoices" element={<PageGate allowed={me.canViewInvoices}><InvoicesPage /></PageGate>} />
            <Route path="/payments" element={<PageGate allowed={me.canViewPayments}><PaymentsPage /></PageGate>} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/about" element={<AboutPage />} />
            {extraRoutes.map(r => (
              <Route key={r.path} path={r.path}
                element={r.agentOnly ? <PageGate allowed={role === 'agent'}>{r.element}</PageGate> : r.element} />
            ))}
          </Routes>

        </div>
      </div>

      {showHelp && <HelpPanel onClose={() => setShowHelp(false)} />}

      {peekId && (
        <RequestPeekPanel
          id={peekId}
          detailCache={detailCache}
          fetchDetail={fetchDetail}
          role={role}
          me={me}
          updateRequest={updateRequest}
          postTransition={postTransition}
          addComment={addComment}
          tweaks={tweaks}
          onClose={() => setPeekId(null)}
        />
      )}
      <TweaksPanel title="Tweaks">
        <TweakSection title="View">
          <TweakRadio label="Default view" value={tweaks.view} onChange={v => setTweak('view', v)} options={[
            { value: 'board', label: 'Board' }, { value: 'timeline', label: 'Timeline' },
          ]} />
          <TweakRadio label="Density" value={tweaks.density} onChange={v => setTweak('density', v)} options={[
            { value: 'compact', label: 'Compact' }, { value: 'comfortable', label: 'Comfortable' },
          ]} />
        </TweakSection>
        <TweakSection title="Display">
          <TweakToggle label="Show priority + SLA" value={tweaks.showPriority} onChange={v => setTweak('showPriority', v)} />
          <TweakToggle label="Dark navy header"    value={tweaks.darkHeader}   onChange={v => setTweak('darkHeader', v)} />
        </TweakSection>
      </TweaksPanel>
    </div>
  );
}

function breadcrumbLabel(page, role) {
  return {
    requests: t('crumb.my_requests'), create: t('nav.new_request'), orders: t('crumb.my_orders'),
    invoices: t('crumb.my_invoices'), payments: t('crumb.my_payments'),
    assets: role === 'agent' ? t('crumb.assets_agent') : t('crumb.my_assets'),
    account: t('account.title'),
    'request-cluster': t('nav.request_cluster'), about: t('crumb.about'), timesheets: 'Timesheets',
    'project-management': 'Project Management', 'team-activity': 'Team Activity',
  }[page] || page;
}